// Universal household detail panel rendered inside the expanded row of the
// Households table. Sub-tabs surface live data per division:
//   • Overview       — at-a-glance snapshot
//   • BWM            — wealth members + Investment Management (SEI placeholder)
//   • BTS            — tax members + TaxDome / ProConnect placeholders
//   • BIA            — live insurance policies for this household
//   • Members        — existing edit / split / merge / rename actions

import { useEffect, useState } from 'react';
import { fetchPolicies, fetchRequests, updateRequest } from '../../utils/insuranceApi';
import { formatCurrency } from '../../utils/formatting';
import RequestDocsModal from '../Insurance/RequestDocsModal';
import AuditHistory from '../common/AuditHistory';

const DIVISION_COLORS = {
  bwm: 'bg-bullogic-blue',
  bts: 'bg-deep-teal',
  bia: 'bg-sky-blue',
};

const TIER_LABELS = {
  'guided-wealth': 'Guided Wealth',
  'total-wealth': 'Total Wealth',
  'investment-only': 'Investment Only',
  'tax-plan': 'Tax Plan',
  'tax-prep': 'Tax Prep',
  'bookkeeping': 'Bookkeeping',
  'insurance': 'Insurance',
};

// Mirrors getAnnualizedRevenue in BWMClients.jsx — annualRevenue is the source
// of truth; only fall back to monthly*12 if annualRevenue is missing. For BWM
// add investmentFeeAnnual + taxPlanFee on top.
function getAnnualizedRevenue(client) {
  if (!client) return 0;
  let base = client.annualRevenue || 0;
  if (base === 0) base = (client.monthlyRevenue || 0) * 12;
  if (client.division === 'bwm') {
    return base + (client.investmentFeeAnnual || 0) + (client.taxPlanFee || 0);
  }
  return base;
}

const PRODUCT_LABELS = {
  auto: 'Auto', home: 'Home', umbrella: 'Umbrella',
  life: 'Life', disability: 'Disability', watercraft: 'Watercraft', wind: 'Wind', flood: 'Flood',
};

const LIFECYCLE_BADGES = {
  in_force: { label: 'In Force', color: 'bg-deep-teal/15 text-deep-teal' },
  future_effective: { label: 'Future Effective', color: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expired', color: 'bg-rose/15 text-rose' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-200 text-gray-600' },
  lapsed: { label: 'Lapsed', color: 'bg-amber-100 text-amber-700' },
};

export default function HouseholdDetail({
  household,
  openEditForm,
  splitFromHousehold,
  renameHousehold,
  onMergeStart,
  openAddService,
  onClose,
}) {
  const [tab, setTab] = useState('overview');
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [policyError, setPolicyError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Close on ESC
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Load insurance policies for this household. Fires when the modal opens
  // (and again if the user switches tabs — covers the case where policies
  // were just uploaded in another tab).
  useEffect(() => {
    let cancelled = false;
    setLoadingPolicies(true);
    setPolicyError(null);
    Promise.all([
      fetchPolicies({ household_id: household.id }).catch(() => []),
      fetchRequests({ household_id: household.id }).catch(() => []),
    ]).then(([pols, reqs]) => {
      if (cancelled) return;
      setPolicies(pols);
      setRequests(reqs);
      setLoadingPolicies(false);
    });
    return () => { cancelled = true; };
  }, [household.id, tab, reloadTick]);

  async function handleRequestStatusChange(req, newStatus) {
    try {
      await updateRequest(req.id, { status: newStatus });
      setReloadTick(t => t + 1);
    } catch (e) {
      window.alert(`Update failed: ${e.message}`);
    }
  }

  const entries = household.entries || [];
  const bwm = entries.filter(e => e.division === 'bwm');
  const bts = entries.filter(e => e.division === 'bts');
  const bia = entries.filter(e => e.division === 'bia');
  const totalAnnual = entries.reduce((s, e) => s + getAnnualizedRevenue(e), 0);
  const inForcePolicies = policies.filter(p => p.lifecycle === 'in_force');
  const annualPremium = inForcePolicies.reduce((s, p) => s + (p.premium_annual || 0), 0);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.18)] w-full max-w-[1100px] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar — uses the bone tint for visual separation from the body */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-bone/40 rounded-t-[4px]">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#888]">Household</div>
            <div className="text-[18px] font-bold text-navy">{household.name}</div>
          </div>
          <div className="flex items-center gap-6">
            <Stat label="Members" value={entries.length} />
            <Stat label="Active Lines" value={household.divisions?.length || 0} />
            <Stat label="Annual Revenue" value={formatCurrency(totalAnnual)} accent="deep-teal" />
            <Stat label="Annual Premium" value={formatCurrency(annualPremium)} accent="bullogic-blue" sub={`${inForcePolicies.length} in-force`} />
            <button
              onClick={onClose}
              className="ml-2 text-[20px] text-[#888] hover:text-navy leading-none px-2"
              title="Close (Esc)"
            >
              ×
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 px-5">
          <SubTab active={tab === 'overview'} onClick={() => setTab('overview')} label="Overview" />
          <SubTab active={tab === 'bwm'} onClick={() => setTab('bwm')} label={`BWM ${bwm.length ? `(${bwm.length})` : ''}`} />
          <SubTab active={tab === 'bts'} onClick={() => setTab('bts')} label={`BTS ${bts.length ? `(${bts.length})` : ''}`} />
          <SubTab active={tab === 'bia'} onClick={() => setTab('bia')} label={`BIA ${policies.length ? `(${policies.length})` : ''}`} />
          <SubTab active={tab === 'members'} onClick={() => setTab('members')} label="Members & Actions" />
          <SubTab active={tab === 'activity'} onClick={() => setTab('activity')} label="Activity" />
        </div>

        {/* Tab content */}
        <div className="p-5 min-h-[280px]">
          {tab === 'overview' && (
            <OverviewTab household={household} bwm={bwm} bts={bts} bia={bia} policies={policies} />
          )}
          {tab === 'bwm' && (
            <BWMTab entries={bwm} openAddService={openAddService} />
          )}
          {tab === 'bts' && (
            <BTSTab entries={bts} openAddService={openAddService} />
          )}
          {tab === 'bia' && (
            <BIATab
              policies={policies}
              loading={loadingPolicies}
              error={policyError}
              requests={requests}
              onRequestDocs={() => setRequestModalOpen(true)}
              onChangeRequestStatus={handleRequestStatusChange}
              biaEntries={bia}
              openAddService={openAddService}
            />
          )}
          {tab === 'members' && (
            <MembersTab
              household={household}
              openEditForm={openEditForm}
              splitFromHousehold={splitFromHousehold}
              renameHousehold={renameHousehold}
              onMergeStart={onMergeStart}
            />
          )}
          {tab === 'activity' && (
            <ActivityTab household={household} />
          )}
        </div>
      </div>

      {requestModalOpen && (
        <RequestDocsModal
          household={household}
          onClose={() => setRequestModalOpen(false)}
          onCreated={() => setReloadTick(t => t + 1)}
        />
      )}
    </div>
  );
}

// ─── Sub-tab content ────────────────────────────────────────────────

function OverviewTab({ household, bwm, bts, bia, policies }) {
  const services = [];
  if (bwm.length) services.push(`BWM (${bwm.map(e => TIER_LABELS[e.serviceTier] || e.serviceTier).filter(Boolean).join(', ') || bwm.length + ' members'})`);
  if (bts.length) services.push(`BTS (${bts.length} member${bts.length !== 1 ? 's' : ''})`);
  if (policies.length) services.push(`BIA (${policies.length} polic${policies.length === 1 ? 'y' : 'ies'})`);
  const notes = household.entries?.map(e => e.notes).filter(Boolean).join(' · ');

  return (
    <div className="space-y-3 text-[12px]">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1">Services</div>
        <div className="text-navy">{services.join(' · ') || '(none)'}</div>
      </div>
      {notes && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1">Notes</div>
          <div className="text-[#444]">{notes}</div>
        </div>
      )}
    </div>
  );
}

function BWMTab({ entries, openAddService }) {
  if (entries.length === 0) {
    return (
      <div className="space-y-3">
        <EmptyState label="No BWM service for this household." />
        {openAddService && (
          <AddServiceButton division="bwm" onClick={openAddService} label="Add Wealth Service" />
        )}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888]">Wealth Members</div>
          {openAddService && (
            <AddServiceButton division="bwm" onClick={openAddService} label="+ Add Wealth Service" small />
          )}
        </div>
        <div className="space-y-1">
          {entries.map(e => (
            <MemberRow key={e.id} entry={e}>
              <span>{TIER_LABELS[e.serviceTier] || e.serviceTier}</span>
              {e.monthlyRevenue > 0 && <span>{formatCurrency(e.monthlyRevenue)}/mo</span>}
              {e.investmentFeeAnnual > 0 && <span>{formatCurrency(e.investmentFeeAnnual)}/yr inv</span>}
              {e.aum > 0 && <span>AUM {formatCurrency(e.aum)}</span>}
            </MemberRow>
          ))}
        </div>
      </div>
      <PlaceholderCard
        title="Investment Accounts (SEI)"
        body="Account-level holdings, performance, and AUM history will populate here once the SEI custodian feed is connected."
      />
    </div>
  );
}

function BTSTab({ entries, openAddService }) {
  if (entries.length === 0) {
    return (
      <div className="space-y-3">
        <EmptyState label="No BTS service for this household." />
        {openAddService && (
          <AddServiceButton division="bts" onClick={openAddService} label="Add Tax Service" />
        )}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888]">Tax Members</div>
          {openAddService && (
            <AddServiceButton division="bts" onClick={openAddService} label="+ Add Tax Service" small />
          )}
        </div>
        <div className="space-y-1">
          {entries.map(e => (
            <MemberRow key={e.id} entry={e}>
              <span>{TIER_LABELS[e.serviceTier] || e.serviceTier}</span>
              {e.taxPlanFee > 0 && <span>Tax Plan {formatCurrency(e.taxPlanFee)}</span>}
              {e.annualRevenue > 0 && <span>{formatCurrency(e.annualRevenue)}/yr</span>}
            </MemberRow>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PlaceholderCard title="TaxDome" body="Active jobs, document requests, and engagement status will land here." />
        <PlaceholderCard title="ProConnect" body="Filing status, refund/balance due, and prior-year returns will land here." />
      </div>
    </div>
  );
}

function BIATab({ policies, loading, error, requests = [], onRequestDocs, onChangeRequestStatus, biaEntries = [], openAddService }) {
  if (loading) return <div className="text-[12px] text-[#888]">Loading policies...</div>;
  if (error) return <div className="text-[12px] text-rose">Error loading policies: {error}</div>;

  const openRequests = requests.filter(r => r.status === 'pending' || r.status === 'sent');
  const recentClosed = requests.filter(r => r.status === 'received' || r.status === 'closed').slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Document request panel */}
      <div className="bg-bone/40 rounded-[4px] p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[11px] font-bold text-navy">Document Requests</div>
            <div className="text-[10px] text-[#888]">
              {openRequests.length === 0
                ? 'No open requests for this household.'
                : `${openRequests.length} open request${openRequests.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <button
            onClick={onRequestDocs}
            className="px-3 py-1.5 text-[11px] font-bold text-white bg-bullogic-blue rounded-[3px] hover:bg-bullogic-blue/90"
          >
            + Request Current Policies
          </button>
        </div>

        {openRequests.length > 0 && (
          <div className="space-y-1.5 mt-2">
            {openRequests.map(r => (
              <RequestRow key={r.id} request={r} onChangeStatus={onChangeRequestStatus} />
            ))}
          </div>
        )}
        {recentClosed.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[#aaa] mb-1">Recently Received</div>
            <div className="space-y-1">
              {recentClosed.map(r => (
                <RequestRow key={r.id} request={r} onChangeStatus={onChangeRequestStatus} compact />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Insurance service membership — separate from policies. A household
          may have policies on file even before being added as a BIA client,
          or vice-versa. Surface the "add service" action here so users can
          attach BIA service from the same place they manage insurance. */}
      {openAddService && biaEntries.length === 0 && (
        <AddServiceButton division="bia" onClick={openAddService} label="+ Add Insurance Service" small />
      )}

      {/* Policies */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-2">Policies</div>
        {policies.length === 0
          ? <EmptyState label="No insurance policies on file yet. Request current policies above." />
          : <PolicyMiniList policies={policies} />}
      </div>
    </div>
  );
}

function PolicyMiniList({ policies }) {
  return (
    <div className="space-y-1">
      {policies.map(p => {
        const lc = LIFECYCLE_BADGES[p.lifecycle];
        return (
          <div key={p.id} className="flex items-center justify-between bg-bone/30 rounded-[3px] px-3 py-2 text-[12px]">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-bold text-navy w-20">{PRODUCT_LABELS[p.product_type] || p.product_type}</span>
              <span className="text-[#666] truncate">{p.carrier_name || 'unknown carrier'}</span>
              <span className="font-mono text-[11px] text-[#888]">{p.policy_number || '--'}</span>
              {p.we_broker && (
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] bg-bullogic-blue/15 text-bullogic-blue">Broker</span>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[11px] text-[#888]">{p.effective_date || '?'} → {p.expiration_date || '?'}</span>
              <span className="font-bold text-navy">{formatCurrency(p.premium_annual || 0)}<span className="text-[9px] text-[#aaa]">/yr</span></span>
              {lc && (
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-[3px] ${lc.color}`}>{lc.label}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const REQUEST_STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  sent: 'bg-bullogic-blue/15 text-bullogic-blue',
  received: 'bg-deep-teal/15 text-deep-teal',
  closed: 'bg-gray-200 text-gray-600',
  cancelled: 'bg-rose/15 text-rose',
};

function RequestRow({ request, onChangeStatus, compact = false }) {
  const sc = REQUEST_STATUS_COLORS[request.status] || 'bg-gray-100 text-gray-600';
  const requested = request.requested_at ? new Date(request.requested_at).toLocaleDateString() : '?';
  const due = request.due_by ? new Date(request.due_by).toLocaleDateString() : null;
  const overdue = request.due_by && (request.status === 'pending' || request.status === 'sent') && new Date(request.due_by) < new Date();

  return (
    <div className={`flex items-center justify-between bg-white rounded-[3px] px-3 py-1.5 text-[11px] border border-gray-200 ${compact ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${sc}`}>{request.status}</span>
        <span className="text-navy">{request.request_type.replace(/_/g, ' ')}</span>
        <span className="text-[#888]">requested {requested}</span>
        {request.recipient_email && <span className="text-[#888]">→ {request.recipient_email}</span>}
        {due && (
          <span className={overdue ? 'text-rose font-bold' : 'text-[#888]'}>
            due {due}{overdue ? ' (overdue)' : ''}
          </span>
        )}
      </div>
      {!compact && (
        <div className="flex items-center gap-1 shrink-0">
          {request.status === 'pending' && (
            <button onClick={() => onChangeStatus?.(request, 'sent')} className="text-[10px] font-bold text-bullogic-blue hover:underline px-1">Mark Sent</button>
          )}
          {(request.status === 'pending' || request.status === 'sent') && (
            <button onClick={() => onChangeStatus?.(request, 'received')} className="text-[10px] font-bold text-deep-teal hover:underline px-1">Mark Received</button>
          )}
          {request.status === 'received' && (
            <button onClick={() => onChangeStatus?.(request, 'closed')} className="text-[10px] font-bold text-[#666] hover:underline px-1">Close</button>
          )}
          {(request.status === 'pending' || request.status === 'sent') && (
            <button onClick={() => onChangeStatus?.(request, 'cancelled')} className="text-[10px] font-bold text-rose hover:underline px-1">Cancel</button>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityTab({ household }) {
  // Pull audit entries for every client in the household plus any policies
  // tied to this household_id. Combine + sort by changed_at desc, render
  // through the generic AuditHistory components stacked.
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-2">
          Client Edits ({household.entries.length} member{household.entries.length !== 1 ? 's' : ''})
        </div>
        <div className="space-y-3">
          {household.entries.map(entry => (
            <div key={entry.id}>
              <div className="text-[11px] text-[#666] mb-1">{entry.name}</div>
              <AuditHistory table="clients" recordId={entry.id} limit={10} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MembersTab({ household, openEditForm, splitFromHousehold, renameHousehold, onMergeStart }) {
  return (
    <div className="space-y-2">
      {household.entries.map(entry => (
        <div key={entry.id} className="flex items-center justify-between text-[12px] py-1.5 px-3 bg-bone/30 rounded-[4px]">
          <div className="flex items-center gap-3">
            <span className={`inline-block px-1.5 py-0.5 rounded-[3px] text-[8px] font-bold text-white ${DIVISION_COLORS[entry.division] || 'bg-gray-400'}`}>
              {entry.division.toUpperCase()}
            </span>
            <span className="text-[10px] text-[#888]">{TIER_LABELS[entry.serviceTier] || entry.serviceTier}</span>
            <span className="text-navy font-bold">{entry.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-[3px] font-bold ${entry.status === 'Active' ? 'bg-deep-teal/10 text-deep-teal' : 'bg-rose/10 text-rose'}`}>
              {entry.status}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-navy font-bold">{formatCurrency(getAnnualizedRevenue(entry))}<span className="text-[9px] text-[#aaa] ml-0.5">/yr</span></span>
            <button
              onClick={() => openEditForm?.(entry)}
              className="text-[9px] font-bold text-bullogic-blue bg-bullogic-blue/10 hover:bg-bullogic-blue/20 px-2 py-1 rounded-[3px]"
            >
              Edit
            </button>
            {household.entries.length > 1 && (
              <button
                onClick={() => splitFromHousehold?.(entry.id)}
                className="text-[9px] font-bold text-[#888] bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-[3px]"
                title="Remove from this household into its own"
              >
                Split
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 mt-1">
        <button
          onClick={() => {
            const newName = prompt('Rename household:', household.name);
            if (newName && newName.trim()) renameHousehold?.(household.id, newName.trim());
          }}
          className="text-[10px] font-bold text-navy bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-[3px]"
        >
          Rename
        </button>
        <button
          onClick={() => onMergeStart?.(household.id)}
          className="text-[10px] font-bold text-bullogic-blue bg-bullogic-blue/10 hover:bg-bullogic-blue/20 px-3 py-1 rounded-[3px]"
        >
          Merge with another household
        </button>
      </div>
    </div>
  );
}

// ─── Tiny presentational helpers ────────────────────────────────────

function Stat({ label, value, accent = 'navy', sub }) {
  const colors = {
    'deep-teal': 'text-deep-teal',
    'bullogic-blue': 'text-bullogic-blue',
    'navy': 'text-navy',
  };
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#888]">{label}</div>
      <div className={`text-[16px] font-bold ${colors[accent] || 'text-navy'}`}>{value}</div>
      {sub && <div className="text-[9px] text-[#888]">{sub}</div>}
    </div>
  );
}

function SubTab({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-[11px] font-bold transition-colors ${
        active
          ? 'text-navy border-b-2 border-bullogic-blue'
          : 'text-[#888] hover:text-navy border-b-2 border-transparent'
      }`}
    >
      {label}
    </button>
  );
}

function MemberRow({ entry, children }) {
  return (
    <div className="flex items-center justify-between text-[12px] bg-bone/30 rounded-[3px] px-3 py-2">
      <div className="flex items-center gap-3">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-[3px] font-bold ${entry.status === 'Active' ? 'bg-deep-teal/10 text-deep-teal' : 'bg-rose/10 text-rose'}`}>
          {entry.status}
        </span>
        <span className="text-navy font-bold">{entry.name}</span>
      </div>
      <div className="flex items-center gap-4 text-[#666]">{children}</div>
    </div>
  );
}

// Lightweight CTA used in each division tab to spin up a new service for
// the open household. The opener pre-fills the household + division so
// the user only has to fill in the revenue details.
function AddServiceButton({ division, onClick, label, small = false }) {
  if (!onClick) return null;
  const colors = {
    bwm: 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90',
    bts: 'bg-deep-teal text-white hover:bg-deep-teal/90',
    bia: 'bg-sky-blue text-white hover:bg-sky-blue/90',
  };
  const cls = colors[division] || colors.bwm;
  const size = small ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-2 text-[11px]';
  return (
    <button
      onClick={() => onClick({ division })}
      className={`${size} font-bold rounded-[3px] ${cls}`}
    >
      {label}
    </button>
  );
}

function EmptyState({ label }) {
  return <div className="text-[12px] text-[#888] italic">{label}</div>;
}

function PlaceholderCard({ title, body }) {
  return (
    <div className="bg-bone/40 border border-dashed border-gray-300 rounded-[4px] p-3">
      <div className="text-[11px] font-bold text-navy mb-1">{title}</div>
      <div className="text-[10px] text-[#888]">{body}</div>
    </div>
  );
}
